import React, { useState, useRef } from 'react';
import Rules from '../../utils/validation_rules';
import classnames from 'classnames';

const Select = ({
    label = '',
    placeholder = null,
    id = 'input',
    validators = [],
    errorMessage = '',
    defaultValue = undefined,
    value = undefined,
    readOnly = false,
    required = false,
    append = '',
    small = false,
    className = '',
    multiple = false,
    collapse = false,
    autoComplete = null,
    showRequired = true,
    disabled = false,
    hideOptional = false,
    onChange = () => {},
    hidden = false,
    children = [],
    defaultEmpty = false,
    prepend = false,
    prependLabel = ''
}) => {
    const [isValid, setIsValid] = useState(true);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const [showErrors, setShowErrors] = useState(false);

    const _input = useRef();

    const validate = () => {
        let valid = true;
        let message = errorMessage || '';
        let rules = validators;
        if (required) {
            rules = ['isRequired', ...validators];
        }

        if (!_input.current.value && !required) {
            setIsValid(valid);
            setErrorText(message);
            return isValid;
        }

        rules.every((rule) => {
            let { func, msg } = Rules[rule];
            let test = func(_input.current);
            valid = valid && test;
            message = msg;
            return isValid;
        });

        setIsValid(valid);
        setErrorText(message);

        return isValid;
    };

    const getValue = () => {
        if (multiple) {
            let vals = [];
            let opts = _input.current.getElementsByTagName('option');
            for (let i = 0; i < opts.length; i++) {
                if (opts[i].selected) {
                    vals.push(opts[i].value);
                }
            }
            return vals;
        }
        return _input.current.value.trim();
    };

    const handleFocus = () => {
        setShowErrors(true);
    };

    const handleChange = (e) => {
        if (!isValid) {
            validate();
        }
        onChange(id, getValue());
    };

    const handleBlur = () => {
        if (validate()) {
            setIsSuccess(true);
        }
        setShowErrors(false);
    };

    let isRequired = required || showRequired;
    let showOptional = !isRequired && !hideOptional;

    if (readOnly) {
        return value;
    }

    return (
        <div className={classnames('form-group', { 'was-validated': !isValid, 'mb-0': collapse }, className)}>
            {label && !prependLabel && (
                <label
                    htmlFor={id}
                    className={classnames({
                        required: isRequired,
                        hidden: hidden,
                        'label-sm': small
                    })}
                >
                    {label}{' '}
                    {showOptional && <span className="text-muted font-weight-normal font-italic">(optional)</span>}
                </label>
            )}
            <div
                className={classnames({
                    'input-group': prepend || prependLabel || append
                })}
            >
                {prepend && (
                    <div className={classnames('input-group-prepend', { 'input-group-sm': small })}>
                        {React.isValidElement(prepend) ? prepend : <span className="input-group-text">{prepend}</span>}
                    </div>
                )}
                {prependLabel && (
                    <div
                        className={classnames('input-group-prepend prepend-label', {
                            'input-group-sm': small
                        })}
                    >
                        <label htmlFor={id} className="input-group-text">
                            {label}
                        </label>
                    </div>
                )}
                <select
                    className={classnames('custom-select', {
                        'form-control': !readOnly,
                        'form-control-plaintext': readOnly,
                        'input-collapse': collapse,
                        success: isValid && isSuccess,
                        error: !isValid && showErrors,
                        'form-control-sm': small,
                        'input-group-control': prepend || prependLabel || append,
                        'input-group-control-prepend': prepend || prependLabel,
                        'input-group-control-append': append
                    })}
                    ref={_input}
                    defaultValue={defaultValue}
                    value={value}
                    name={id}
                    id={id}
                    required={required}
                    multiple={multiple}
                    autoComplete={autoComplete}
                    readOnly={readOnly}
                    disabled={disabled}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleChange}
                >
                    {defaultEmpty && <option value="">Select...</option>}
                    {children}
                </select>

                {multiple && (
                    <small className="form-text text-muted">
                        <code>{window.navigator.platform.includes('Mac') ? 'command' : 'ctrl'} + click</code> to select
                        multiple
                    </small>
                )}

                {!isValid && (
                    <div className="form-error">
                        <span>{errorText}</span>
                    </div>
                )}
            </div>
        </div>
    );
};
export default Select;
